// src/pages/project/ProjectSettingsComponentsPage.tsx
// Components settings: ListComponents + Create / Get / Update / Delete.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useProjectComponent, useI18n } from '@react/hooks';
import { Button, Input, Textarea, Card, CardContent, Badge, ErrorAlert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ColorPicker } from '@/components/ColorPicker';
import { ProjectModal, Field } from '@/features/project/ProjectModal';
import { idOf, listParams } from '@/features/project/meta';
import { getDisplayName, PROJECT_ACCENT_COLORS } from '@/features/project/logic';
import type { Entity } from '@core/types/entity';
import type { ProjectComponent } from '@core/types/project';

export const ProjectSettingsComponentsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const projectStore = useProject();
  const store = useProjectComponent();
  const { t } = useI18n();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ProjectComponent | null>(null);
  const [detail, setDetail] = useState<ProjectComponent | null>(null);
  const [deleting, setDeleting] = useState<ProjectComponent | null>(null);
  const [busy, setBusy] = useState(false);

  const projectEntity = (projectStore.currentItem as never as { entity?: Entity } | null)?.entity;

  const reload = () => id && store.ListComponents(id, listParams());
  useEffect(() => {
    if (id && !projectStore.currentItem) projectStore.GetProject(id);
    reload();
  }, [id]);

  const openDetail = async (component: ProjectComponent) => {
    if (!id) return;
    setDetail(component);
    try {
      const full = await store.GetComponent(id, idOf(component));
      setDetail(full);
    } catch {
      /* keep list version */
    }
  };

  const components: ProjectComponent[] = store.items ?? [];

  const del = async () => {
    if (!id || !deleting) return;
    setBusy(true);
    try {
      await store.DeleteComponent(id, idOf(deleting));
      setDeleting(null);
      reload();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('project.section.components')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('project.section.componentsDesc')}</p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          {t('project.component.create')}
        </Button>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {components.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('project.component.empty')}</p>
      ) : (
        <div className="space-y-3">
          {components.map((c) => (
            <Card key={idOf(c)}>
              <CardContent className="flex items-center justify-between gap-3 p-4">
                <button
                  type="button"
                  onClick={() => openDetail(c)}
                  className="flex min-w-0 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {c.color && <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: c.color }} aria-hidden="true" />}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{c.name}</p>
                    {c.description && <p className="truncate text-xs text-muted-foreground">{c.description}</p>}
                  </div>
                  <Badge variant="secondary" size="sm">
                    {c.item_count ?? 0}
                  </Badge>
                </button>
                <div className="flex flex-shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditing(c);
                      setShowForm(true);
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setDeleting(c)}>
                    {t('common.delete')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && projectEntity && (
        <ComponentFormDialog
          projectEntity={projectEntity}
          component={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            reload();
          }}
        />
      )}

      {detail && (
        <ProjectModal open title={detail.name} onClose={() => setDetail(null)}>
          <div className="space-y-3">
            {detail.color && (
              <span className="inline-block h-4 w-4 rounded-sm" style={{ backgroundColor: detail.color }} aria-hidden="true" />
            )}
            {detail.description && <p className="text-sm text-muted-foreground">{detail.description}</p>}
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">{t('project.component.items')}</p>
              <p className="mt-0.5 text-lg font-semibold text-foreground">{detail.item_count ?? 0}</p>
            </div>
            {detail.lead && <p className="text-sm text-muted-foreground">{getDisplayName(detail.lead)}</p>}
          </div>
        </ProjectModal>
      )}

      <ConfirmDialog
        open={!!deleting}
        title={t('project.component.deleteTitle')}
        description={t('project.component.deleteDesc')}
        destructive
        isConfirming={busy}
        onConfirm={del}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
});

function ComponentFormDialog({
  projectEntity,
  component,
  onClose,
  onSaved,
}: {
  projectEntity: Entity;
  component: ProjectComponent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectComponent();
  const [name, setName] = useState(component?.name ?? '');
  const [description, setDescription] = useState(component?.description ?? '');
  const [color, setColor] = useState(component?.color ?? '#6366f1');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (component) {
        await store.UpdateComponent(projectEntity.id, idOf(component), {
          project: projectEntity,
          component: { ...component, name: name.trim(), description, color },
          field_mask: { paths: ['name', 'description', 'color'] },
        } as never);
      } else {
        await store.CreateComponent(projectEntity.id, {
          project: projectEntity,
          name: name.trim(),
          description: description || undefined,
          color,
        } as never);
      }
      onSaved();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProjectModal
      open
      title={component ? t('project.component.editTitle') : t('project.component.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <Field label={t('project.component.name')} htmlFor="comp-name">
          <Input id="comp-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('project.component.description')} htmlFor="comp-desc">
          <Textarea id="comp-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </Field>
        <Field label={t('project.component.color')}>
          <ColorPicker options={PROJECT_ACCENT_COLORS} value={color} onChange={setColor} />
        </Field>
      </div>
    </ProjectModal>
  );
}

export default ProjectSettingsComponentsPage;
